import React from 'react';

import { ReactComponent as VerifyIdGraphic } from '../../../../assets/graphics/verify-id.svg';
import Text from '../../../../components/Typography/Text';
import Button from '../../../../components/Buttons/Button';
import { LinearProgress } from '@material-ui/core';
import { useAccountContext } from '../Data/accountContext';

import { dateFieldFormat } from '../../../../lib/format/format';
import { formatPbDate } from '../../../../lib/fmt';
import { notifyError } from '../../../../components/Messages/Notification';
import { getJumioStatus, getUrl, mockJumioData } from '../../../../services/KycService';
import { saveRegistrationKyc } from '../../../../services/RegistrationService';
import VerificationModal from '../../../../components/Modals/VerificationModal';
import { MOCK_JUMIO } from '../../../../lib/data/common';

const FormIdentificationJumio = () => {
  const { account, viewOnly, isLoading, setIsLoading, setAccountResponse } =
    useAccountContext();

  const [verificationData, setVerificationData] = React.useState({
    transactionReference: '',
    redirectUrl: '',
    submittedVerification: false,
    status: '',
    result: false,
    id: 0,
  });

  const [open, setOpen] = React.useState(false);

  const [jumioSatusResult, setJumioSatusResult] = React.useState('');

  const setMessage = (result, rejectReason, screeningStatus) => {
    const statusMessage =
      (result ? `Result: ${result}<br>` : '') +
      (rejectReason ? `Reason: ${rejectReason}<br>` : '') +
      (screeningStatus ? `Screening Status: ${screeningStatus}<br>` : '');
    if (statusMessage) {
      setJumioSatusResult(
        `${statusMessage}Please try to reverify or use a different ID.`
      );
    } else {
      setJumioSatusResult('');
    }
  };

  async function fetchJumioData(ctr, data) {
    if (!data.transactionReference || data.transactionReference === '') {
      return;
    }
    if (ctr === 3) {
      notifyError('Unable to get result please try again ');
      setIsLoading(false);
      clearVerification();
      return;
    }
    ctr++;
    try {
      const dt = await getJumioStatus(data);
      setJumioResult(data, dt);
    } catch (e) {
      console.error(e);
    }
  }

  React.useEffect(() => {
    if (account.accountId && account.kycStatus != 'Approved') {
      getVerificationResult();
    }
    // eslint-disable-next-line
  }, [account.accountId]);
  const getVerificationResult = async () => {
    try {
      const res = await getJumioStatus({
        accountId: account.accountId,
        transactionReference: localStorage.getItem('transactionReference'),
      });
      if (res.idType !== '') {
        setJumioResult({}, res);
      }
      if (!['PASSED', 'WARNING'].includes(res?.screeningStatus) && res?.result !== '') {
        setMessage(res?.result, res?.rejectReason, res?.screeningStatus);
      } else {
        setMessage();
      }
    } catch (error) {
      console.error(error.message || error);
    }
  };

  const setJumioResult = async (jumioData, jumioStatus) => {
    if (jumioStatus?.result === '') {
      notifyError('Unable to get result please try again ');
      setIsLoading(false);
      clearVerification();
      return;
    }

    try {
      let ownerIdentification = { ...account.primaryOwner.identification };

      if (['PASSED', 'APPROVED_VERIFIED'].includes(jumioStatus?.result)) {
        jumioData.result = true;

        localStorage.setItem('verifyResult', jumioStatus?.result);
        ownerIdentification.identificationNo = jumioStatus?.identificationNo;
        if (jumioStatus.idType === 'DRIVING_LICENSE') {
          ownerIdentification.identificationType = 'Driver License';
        } else if (jumioStatus.idType === 'PASSPORT') {
          ownerIdentification.identificationType = 'Passport';
        } else if (jumioStatus.idType === 'ID_CARD') {
          ownerIdentification.identificationType = 'Other Government Id';
        }

        ownerIdentification.issueDate = jumioStatus?.issueDate
          ? dateFieldFormat(formatPbDate(jumioStatus?.issueDate))
          : '';
        ownerIdentification.expirationDate = jumioStatus?.expiry
          ? dateFieldFormat(formatPbDate(jumioStatus?.expiry))
          : '';
        ownerIdentification.dateOfBirth = jumioStatus?.idDob
          ? dateFieldFormat(formatPbDate(jumioStatus?.idDob))
          : '';

        ownerIdentification.countryOfIssuance = jumioStatus?.idCountry.substr(
          0,
          jumioStatus.idCountry.length > 2 ? 2 : jumioStatus.idCountry.length
        );

        ownerIdentification.imgGovernmentId = jumioStatus.idImg;
        ownerIdentification.state = jumioStatus.state;

        if (['PASSED', 'DONE'].includes(jumioStatus.screeningStatus)) {
          ownerIdentification.kycStatus = 'Approved';
          setMessage();
        } else if (['WARNING'].includes(jumioStatus.screeningStatus)) {
          ownerIdentification.kycStatus = 'Warning';
          setMessage();
        } else {
          ownerIdentification.kycStatus = 'Denied';
          ownerIdentification.statusReason =
            'Jumio screening status is ' + jumioStatus.screeningStatus;

          setMessage(
            jumioStatus?.result,
            jumioStatus?.rejectReason,
            jumioStatus?.screeningStatus
          );
          clearVerification();
        }

        if (
          ownerIdentification.kycStatus === 'Approved' &&
          !['DRIVING_LICENSE', 'PASSPORT', 'ID_CARD'].includes(jumioStatus.idType)
        ) {
          ownerIdentification.kycStatus = 'Denied';
          ownerIdentification.statusReason =
            'Unsupported ID type, please use Driver License, Passport, or National Id';
        }
        setVerificationData(jumioData);
        setIsLoading(false);
      } else {
        let rejectReason = !jumioStatus?.rejectReason
          ? jumioStatus?.result
          : jumioStatus?.rejectReason;
        notifyError('Invalid Verification: ' + rejectReason);
        setMessage(
          jumioStatus?.result,
          jumioStatus?.rejectReason,
          jumioStatus?.screeningStatus
        );
        setIsLoading(false);
        ownerIdentification.kycStatus = 'Denied';
        ownerIdentification.statusReason = 'JUMIO result is: ' + rejectReason;

        clearVerification();
      }

      const res = await saveRegistrationKyc(ownerIdentification);
      setAccountResponse(res);
    } catch (error) {
      notifyError(error?.message || error);
    }
  };

  const clearVerification = () => {
    localStorage.removeItem('transactionReference');
    localStorage.removeItem('redirectUrl');
    localStorage.removeItem('verifyStatus');
    localStorage.removeItem('verifyResult');

    let data = { ...verificationData };

    data.transactionReference = '';
    data.result = false;
    data.status = '';
    data.redirectUrl = '';

    setVerificationData(data);
  };

  const handleVerificationClick = async (e) => {
    // FOR DEBUGGING
    if (MOCK_JUMIO) {
      setIsLoading(true);
      const { data, status } = await mockJumioData();
      return setJumioResult(data, status);
    }

    let verifyData = { ...verificationData };

    if (verifyData.status === 'success') {
      handleCloseVerification(verifyData);
      return;
    }

    if (verifyData.redirectUrl === '') {
      verifyData.status = '';
      verifyData.id = account.accountId;

      try {
        const data = await getUrl(verifyData);

        verifyData.redirectUrl = data.redirectUrl;
        verifyData.transactionReference = data.transactionReference;
      } catch (e) {
        console.error(e);
        return;
      }
    }

    verifyData.id = account.accountId;

    setVerificationData(verifyData);
    setOpen(true);
  };

  const handleCloseVerification = async (data) => {
    if (data.transactionReference !== '') {
      localStorage.setItem('transactionReference', data.transactionReference);
      localStorage.setItem('redirectUrl', data.redirectUrl);
    }

    localStorage.setItem('verifyStatus', data.status);

    if (data.status === 'error' || data.status === '') {
      notifyError('Error in verification. Please try again');
      clearVerification();
      return;
    }

    let fetchCtr = 0;

    if (data.status === 'success') {
      setOpen(false);
      setIsLoading(true);
      if (data.transactionReference === '') return;
      setTimeout(async function () {
        await fetchJumioData(fetchCtr, data);
      }, 50000);
    } else {
      setOpen(false);
    }
  };

  return (
    <React.Fragment>
      <div className="grd-row">
        <Text
          mb={10}
          label="Government ID - US Driver's License or International Passport"
        />
      </div>
      <div className="grd-row verify-c nm">
        <div className="grd-cell-none">
          <VerifyIdGraphic />
          <Text
            mt={10}
            mb={10}
            variant="h2"
            label={isLoading ? 'Verifying...' : 'Identity Verification'}
          />
          <Text
            mb={25}
            variant="body1"
            label={
              isLoading
                ? 'Please wait while we verify your identity'
                : 'We need to verify your information to process your application'
            }
          />
          {isLoading ? (
            <>
              <Text variant="h2" label="This may take up to 60 seconds." />
              <div className="grd-row"></div>
              <LinearProgress variant="indeterminate" color="secondary" />
            </>
          ) : (
            <>
              <Button
                loading={isLoading}
                disabled={isLoading || verificationData.result || viewOnly}
                label={isLoading ? 'Verifying...' : 'Verify Identity'}
                onClick={handleVerificationClick}
              />
            </>
          )}
        </div>
      </div>
      {jumioSatusResult !== '' && (
        <div className="grd-row">
          <Text
            mb={10}
            label={
              <div
                style={{ color: 'red' }}
                dangerouslySetInnerHTML={{ __html: jumioSatusResult }}
              />
            }
          />
        </div>
      )}
      {open && (
        <VerificationModal
          onClose={handleCloseVerification}
          open={open}
          value={verificationData}
        ></VerificationModal>
      )}
    </React.Fragment>
  );
};

export default FormIdentificationJumio;
